import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { 
  UserGroupIcon, 
  DocumentPlusIcon, 
  CalendarIcon,
  BriefcaseIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

const CRMActionNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const actionType = data.config?.actionType || 'create_customer';
  
  // Icon kiválasztása action típus alapján
  const getIcon = (type: string) => {
    const iconMap: Record<string, React.ComponentType<any>> = {
      create_customer: UserGroupIcon,
      update_customer: UserGroupIcon,
      create_project: BriefcaseIcon,
      update_project: BriefcaseIcon,
      create_task: ClipboardDocumentListIcon,
      create_document: DocumentPlusIcon,
      create_calendar_event: CalendarIcon,
    };
    return iconMap[type] || UserGroupIcon;
  };

  // Action típus címkéje
  const getActionLabel = (type: string) => {
    const labelMap: Record<string, string> = {
      create_customer: 'Ügyfél létrehozás',
      update_customer: 'Ügyfél frissítés',
      create_project: 'Projekt létrehozás',
      update_project: 'Projekt frissítés',
      create_task: 'Feladat létrehozás',
      create_document: 'Dokumentum generálás',
      create_calendar_event: 'Naptár esemény',
    };
    return labelMap[type] || type;
  };

  const Icon = getIcon(actionType);

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-cyan-400 to-cyan-500 border-2 transition-all duration-200 ${
      selected ? 'border-cyan-600 shadow-xl scale-105' : 'border-cyan-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-cyan-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Action type */}
          <div className="text-xs text-cyan-100 mt-1">
            {getActionLabel(actionType)}
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Configuration preview */}
      {data.config && Object.keys(data.config).length > 1 && (
        <div className="mt-2 text-xs text-cyan-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="space-y-1">
            {Object.entries(data.config)
              .filter(([key]) => key !== 'actionType')
              .slice(0, 2)
              .map(([key, value]) => (
                <div key={key} className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-cyan-200 rounded-full" />
                  <span className="truncate">
                    {key}: {typeof value === 'string' ? value : JSON.stringify(value)}
                  </span>
                </div>
              ))
            }
          </div>
        </div>
      )}

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Execution result */}
      {data.executionData && (
        <div className="mt-2 text-xs text-cyan-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              data.executionData.success ? 'bg-green-400' : 'bg-red-400'
            }`} />
            <span>
              {data.executionData.success ? 'Sikeresen végrehajtva' : 'Sikertelen'}
            </span>
          </div>
          {data.executionData.id && (
            <div className="font-mono text-xs mt-1">
              ID: {data.executionData.id}
            </div>
          )}
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="data"
        className="w-3 h-3 bg-white border-2 border-cyan-500"
        style={{ left: -6 }}
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="result"
        className="w-3 h-3 bg-white border-2 border-cyan-500"
        style={{ right: -6 }}
      />
    </div>
  );
};

export default CRMActionNode;
